import { GitCompareArrows } from 'lucide-react';

import { Icon } from '@@/Icon';

import { EnvironmentReconcileStatus } from '../types';

import { describeOperation, describeResource, parsePatch } from './driftPatch';

type DriftEntry = NonNullable<EnvironmentReconcileStatus['drift']>[number];

/**
 * Shows what actually drifted, rather than how much.
 *
 * The patch is produced with secrets masked before it is built, so it is safe to
 * display. Knowing that three resources drifted is not actionable; knowing that
 * replicas went from 2 to 5 is.
 */
export function DriftDetails({ drift }: { drift: Array<DriftEntry> }) {
  if (!drift.length) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-col gap-2">
      {drift.map((entry) => (
        <DriftedResource key={entry.id} entry={entry} />
      ))}
    </div>
  );
}

function DriftedResource({ entry }: { entry: DriftEntry }) {
  const operations = parsePatch(entry.patch);

  return (
    <div
      className="rounded border border-warning-5 bg-warning-2 px-2 py-1.5 th-highcontrast:border-warning-7 th-highcontrast:bg-black th-dark:border-warning-7 th-dark:bg-warning-9"
      data-cy={`workflow-drift-${entry.id}`}
    >
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <Icon icon={GitCompareArrows} size="xs" className="shrink-0" />
        {describeResource(entry.id)}
      </div>

      {operations ? (
        <ul className="m-0 mt-1 list-none p-0">
          {operations.map((operation, index) => (
            <li
              // The patch has no stable identifier per operation, and its order
              // is the order it will be applied in.

              key={index}
              // Wrapped rather than left to run on: a drifted label map is one
              // long line, and a modal that scrolls sideways hides the start of
              // every other line to show the end of one.
              className="font-mono text-xs break-all whitespace-pre-wrap text-gray-10 th-highcontrast:text-white th-dark:text-white"
            >
              {describeOperation(operation)}
            </li>
          ))}
        </ul>
      ) : (
        <pre className="m-0 mt-1 font-mono text-xs break-all whitespace-pre-wrap text-gray-10 th-highcontrast:text-white th-dark:text-white">
          {entry.patch}
        </pre>
      )}
    </div>
  );
}
